import {Tr, TrSpace} from "@/i18n/i18n";
import Container = Phaser.GameObjects.Container;
import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {SquareButton} from "@/scenes.ui/square-button";
import {PenaltyDto, RewardDto} from "@/store/battle/types";
import ResourceUtils from "@/utils/resource-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import OnceCaller from "@/utils/once-caller";

export class BattleResultPanel extends Container {

    private readonly DEPTH: number = 100;

    private onAcceptCallback: ((hasWon: boolean) => unknown) | undefined;

    private canvas!: HTMLCanvasElement;

    private rewards!: RewardDto | undefined;
    private penalty!: PenaltyDto | undefined;

    constructor(scene: Scene, canvas: HTMLCanvasElement, hasWon: boolean, onAcceptCallback: (hasWon: boolean) => unknown,
                rewards?: RewardDto, penalty?: PenaltyDto) {
        super(scene);

        this.scene = scene;
        this.canvas = canvas;

        this.onAcceptCallback = onAcceptCallback;
        this.rewards = rewards;
        this.penalty = penalty;

        this.setDepth(this.DEPTH);

        let resultWindowBackgroundImage = SpriteUtils.addSprite(this.scene, 0, 0, Keys.IMG_BLACK_BACKGROUND)
            .setOrigin(0,0)
            .setInteractive();
        SpriteUtils.setDispSize(resultWindowBackgroundImage, canvas!.width, canvas!.height);

        this.add([resultWindowBackgroundImage])

        if (hasWon) {
            this.createVictoriousWindow();
        } else {
            this.createDefeatWindow();
        }

    }

    private createVictoriousWindow() {
        let knight = SpriteUtils.addSprite(this.scene,  CU.ETALON_CANVAS_WIDTH / 2 + 20, this.canvas!.height / 2 / CU.S - 200, Keys.IMG_VICTORY_KNIGHT);
        let ribbon = SpriteUtils.addSprite(this.scene,  CU.ETALON_CANVAS_WIDTH / 2, 70, Keys.ATLAS_UI, Keys.IMG_VICTORY_RIBBON);
        let ribbonText = TextUtils.addTextAdvanced(this.scene, Tr('slavnaja.pobeda'), ribbon.x / CU.S, ribbon.y / CU.S,
            '#ffffff', 30, 'normal', this.canvas!.width, 'center', 1, Fonts.ARIAL_MT)
            .setOrigin(0.5, 0.8);

        let affectLineBack = SpriteUtils.addSprite(this.scene, CU.ETALON_CANVAS_WIDTH / 2, knight.y / CU.S + 300, Keys.ATLAS_PROFILE, Keys.IMG_AFFECT_BACKGROUND);

        let rewardText = TextUtils.addText(this.scene, Tr('vi.poluchaete') + ': ', affectLineBack.x / CU.S, affectLineBack.y / CU.S - 50,
            '#d7d7d7', 25, 'normal', 400);

        let acceptButton = new SquareButton(this.scene, this.canvas!.width / 2 / CU.S, affectLineBack.y / CU.S + 200, -13, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('prinjat'), false, 0,0, 38);

        acceptButton.setCallback(() => {
            OnceCaller.doOnce("acceptBattleRes", () => {
                if(this.onAcceptCallback != null) {
                    this.onAcceptCallback(true);
                }
            }, 500)
        });

        this.add([knight, ribbon, ribbonText, affectLineBack, rewardText, acceptButton]);

        this.showRewards(affectLineBack.x / CU.S, affectLineBack.y / CU.S, this.rewards!);

    }

    private createDefeatWindow() {
        let skull = SpriteUtils.addSprite(this.scene,  CU.ETALON_CANVAS_WIDTH / 2 + 20, this.canvas!.height / CU.S / 2 - 200, Keys.IMG_LOSE_SKULL);
        let ribbon = SpriteUtils.addSprite(this.scene,  CU.ETALON_CANVAS_WIDTH / 2, 70, Keys.ATLAS_UI, Keys.IMG_LOSE_RIBBON);
        let ribbonText = TextUtils.addTextByFamily(this.scene, Tr('porazhenie'), ribbon.x / CU.S, ribbon.y / CU.S,
            '#ffffff', 30, 'normal', 400, Fonts.ARIAL_MT).setOrigin(0.5, 0.75);

        let affectLineBack = SpriteUtils.addSprite(this.scene, CU.ETALON_CANVAS_WIDTH / 2, skull.y / CU.S + 300, Keys.ATLAS_PROFILE, Keys.IMG_AFFECT_BACKGROUND);

        let healthAffectText = TextUtils.addText(this.scene, TrSpace('zdorove').toUpperCase() + '-' + String(this.penalty!.health), affectLineBack.x / CU.S, affectLineBack.y / CU.S - 50,
            '#821b20', 25, 'normal', 400);

        let healthAffectIcon = SpriteUtils.addSprite(this.scene, healthAffectText.x / CU.S - healthAffectText.width / CU.S / 2 - 30, healthAffectText.y / CU.S,
            Keys.ATLAS_UI, Keys.IMG_BATTLE_RESULT_HEARTH);

        let recoverSec: number = this.penalty!.recoverSec;
        let healthAffectTimer = TextUtils.addText(this.scene,
            recoverSec === 60 ? Tr('zdorove.cherez.min') : Tr('zdorove.cherez') + String(recoverSec) + Tr('sek').toUpperCase(),
            affectLineBack.x / CU.S, affectLineBack.y / CU.S + 50, '#d7d7d7', 15, 'normal', 400);

        let acceptButton = new SquareButton(this.scene, CU.ETALON_CANVAS_WIDTH / 2, affectLineBack.y / CU.S + 200, -13, Keys.UI_ART_BROWN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('prinjat'), false, 0,0, 38);

        acceptButton.setCallback(() => {
            if(this.onAcceptCallback != null) {
                this.onAcceptCallback(false);
            }
        });

        this.add([skull, ribbon, ribbonText, affectLineBack, healthAffectText, healthAffectIcon, healthAffectTimer, acceptButton]);

        if (this.penalty!.cups !== 0) {
            let cupsAffectText = TextUtils.addText(this.scene, '-' + String(this.penalty!.cups), affectLineBack.x / CU.S + 10, affectLineBack.y / CU.S,
                '#ff5e65', 25, 'normal', 400);
            let cupsAffectIcon = SpriteUtils.addSprite(this.scene, cupsAffectText.x / CU.S - cupsAffectText.width / CU.S / 2 - 30, cupsAffectText.y / CU.S, Keys.IMG_REWARD_CUPS);
            this.add([cupsAffectText, cupsAffectIcon]);
        } else {
            let healthAffectFiller = TextUtils.addText(this.scene, Tr('zdorove.geroev.vremenno.umenshaetsja'), affectLineBack.x / CU.S, affectLineBack.y / CU.S,
                '#d7d7d7', 15, 'normal', CU.ETALON_CANVAS_WIDTH);
            this.add(healthAffectFiller);
        }
    }

    private showRewards(x: number, y: number, rewards: RewardDto) {

        if (rewards) {
            let shiftX = 0;
            let addition = (rewards.expAddition && rewards.expAddition > 0) ? (' +' + String(rewards.expAddition)) : undefined;
            if (addition) {
                shiftX = -40;
            }
            let expText = TextUtils.addText(this.scene, Tr('opit.plus') + String(Math.floor(rewards.experience)), x + 15 + shiftX, y,
                '#d4b25b', 25, 'normal', 400);
            if (addition) {
                let expAddText = TextUtils.addTextAdvanced(this.scene, addition, expText.getRightCenter().x! / CU.S + 7, y,
                    '#68ff47', 25, 'normal', 200, 'left', 2, 'Arial').setOrigin(0, 0.5);
                this.add(expAddText);
            }

            let expIcon = SpriteUtils.addSprite(this.scene, expText.getLeftCenter().x! / CU.S - 25, expText.y / CU.S, Keys.IMG_REWARD_EXPERIENCE)
                .setOrigin(0.5, 0.55);

            const iconWidth = 56 * CU.S;
            const distanceBetweenIcons = 55 / CU.S;
            // @ts-ignore
            let valuesAboveZero = Object.entries(rewards).filter(([key, value]) => key != 'experience' && value > 0).length
            let rewardsLength = iconWidth * (valuesAboveZero - (addition ? 1 : 0)) + distanceBetweenIcons * (valuesAboveZero - 1 - (addition ? 1 : 0));
            let offsetX = (rewardsLength / 2);

            let rewardType: string, rewardValue: number;
            // @ts-ignore
            for ([rewardType, rewardValue] of Object.entries(rewards)) {

                if (rewardValue > 0 && rewardType != 'experience' && rewardType != 'expAddition') {
                    let atlas = ResourceUtils.findRewardTypeAtlas(rewardType);
                    let rewardTypeIcon;
                    if (atlas) {
                        rewardTypeIcon = SpriteUtils.addSprite(this.scene, x - offsetX, y + 50, atlas, ResourceUtils.findRewardTypeIcon(rewardType));
                        SpriteUtils.setDispSize(rewardTypeIcon, 56 * CU.S, 56 * CU.S);
                    } else {
                        rewardTypeIcon = SpriteUtils.addSprite(this.scene, x - offsetX, y + 50, ResourceUtils.findRewardTypeIcon(rewardType));
                        SpriteUtils.setDispSize(rewardTypeIcon, 56 * CU.S, 56 * CU.S);
                    }

                    let rewardString = TextUtils.formatMoney(TextUtils.trimDigitsAfter(rewardValue, 3));
                    let rewardValueText = TextUtils.addText(this.scene, rewardString, (x - offsetX + rewardTypeIcon.displayWidth / CU.S + (rewardString.length>3 ? 4 : 0)), (y + 50),
                        '#d4b25b', 23, 'normal', 400);

                    this.add(rewardTypeIcon);
                    this.add(rewardValueText);

                    offsetX -= distanceBetweenIcons + (rewardTypeIcon.displayWidth + 10);
                }
            }

            this.add([expIcon, expText]);
        }
    }

}
